/**
 * Register — Códice Botánico edition.
 * Native + codex styling (no MUI). All logic preserved: nutritionist-email
 * validation (debounced), password strength, role select, authService.register.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import authService from '../../services/authService';
import { API_ENDPOINTS } from '../../config/api';
import { AuthShell, Field, PasswordField } from './AuthShell';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '', username: '', password: '', confirm_password: '',
    first_name: '', last_name: '', phone: '', role: 'patient', nutritionist_email: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(null);
  const [nutri, setNutri] = useState({ validating: false, valid: null, message: '', nutritionist: null });

  const validateNutritionistEmail = useCallback(async (email) => {
    if (!email || !authService.validateEmail(email)) {
      setNutri({ validating: false, valid: false, message: 'Ingresa un correo válido', nutritionist: null });
      return;
    }
    setNutri((p) => ({ ...p, validating: true }));
    try {
      const r = await axios.get(`${API_ENDPOINTS.AUTH}/validate-nutritionist/${encodeURIComponent(email)}`);
      setNutri({ validating: false, valid: true, message: r.data.message, nutritionist: r.data.nutritionist });
    } catch (error) {
      setNutri({ validating: false, valid: false, message: error.response?.data?.detail || 'No se pudo validar el correo del nutriólogo', nutritionist: null });
    }
  }, []);

  useEffect(() => {
    if (formData.role === 'patient' && formData.nutritionist_email) {
      const t = setTimeout(() => validateNutritionistEmail(formData.nutritionist_email), 500);
      return () => clearTimeout(t);
    }
    setNutri({ validating: false, valid: null, message: '', nutritionist: null });
    return undefined;
  }, [formData.nutritionist_email, formData.role, validateNutritionistEmail]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
    setGeneralError('');
    if (name === 'password') setPasswordStrength(authService.getPasswordStrength(value));
    if (name === 'role' && value !== 'patient') {
      setFormData((p) => ({ ...p, nutritionist_email: '' }));
      setNutri({ validating: false, valid: null, message: '', nutritionist: null });
    }
  };

  const validate = () => {
    const e = {};
    if (!formData.email) e.email = 'El correo es obligatorio';
    else if (!authService.validateEmail(formData.email)) e.email = 'Correo inválido';
    if (!formData.username || formData.username.length < 3) e.username = 'Mínimo 3 caracteres';
    if (!formData.first_name) e.first_name = 'El nombre es obligatorio';
    if (!formData.last_name) e.last_name = 'El apellido es obligatorio';
    if (formData.role === 'patient') {
      if (!formData.nutritionist_email) e.nutritionist_email = 'Requerido para pacientes';
      else if (!nutri.valid) e.nutritionist_email = 'Ingresa un correo de nutriólogo válido';
    }
    const pw = authService.validatePassword(formData.password);
    if (!pw.isValid) e.password = pw.errors.join('. ');
    if (formData.password !== formData.confirm_password) e.confirm_password = 'Las contraseñas no coinciden';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await authService.register(formData);
      navigate('/auth/login', { state: { message: '¡Cuenta creada! Inicia sesión para continuar.' } });
    } catch (error) {
      setGeneralError(error.message || 'No se pudo crear la cuenta');
    } finally {
      setLoading(false);
    }
  };

  const nutriEnd = nutri.validating
    ? <Spinner />
    : nutri.valid === true ? <OkGlyph />
    : (nutri.valid === false && formData.nutritionist_email) ? <BadGlyph /> : null;

  return (
    <AuthShell
      wide
      kicker="Crear cuenta · 100% gratis"
      title="Crea tu cuenta"
      subtitle="¿Eres paciente o nutriólogo? Elige tu perfil y empieza hoy. Sin tarjeta, sin costo."
      asideTitle="Nutrición para los dos lados de la consulta."
      asideText="Nutriólogos: expediente clínico, planes SMAE y seguimiento de tus pacientes. Pacientes: tu plan de alimentación, tu progreso y Huitzi como guía — gratis, como labor social de Scram."
    >
      {generalError && <div style={s.error}>⚠ {generalError}</div>}

      <form onSubmit={handleSubmit} noValidate>
        <SectionTitle>Cuenta</SectionTitle>
        <Field label="Correo electrónico" name="email" type="email" value={formData.email}
          onChange={handleChange} error={errors.email} disabled={loading} required
          hint="Nunca compartiremos tu correo." />
        <Field label="Usuario" name="username" value={formData.username}
          onChange={handleChange} error={errors.username} disabled={loading} required
          hint="Elige un nombre de usuario único." />

        <SectionTitle>Datos personales</SectionTitle>
        <div style={s.row}>
          <Field label="Nombre" name="first_name" value={formData.first_name}
            onChange={handleChange} error={errors.first_name} disabled={loading} required />
          <Field label="Apellido" name="last_name" value={formData.last_name}
            onChange={handleChange} error={errors.last_name} disabled={loading} required />
        </div>
        <Field label="Teléfono" name="phone" value={formData.phone}
          onChange={handleChange} disabled={loading} hint="Opcional — para notificaciones." />

        <SectionTitle>Tipo de cuenta</SectionTitle>
        <div style={s.roleRow} role="radiogroup" aria-label="Tipo de cuenta">
          <RoleCard active={formData.role === 'patient'} onClick={() => handleChange({ target: { name: 'role', value: 'patient' } })}
            title="Paciente" sub="Busco guía nutricional" Icon={PersonGlyph} disabled={loading} />
          <RoleCard active={formData.role === 'nutritionist'} onClick={() => handleChange({ target: { name: 'role', value: 'nutritionist' } })}
            title="Nutriólogo" sub="Cuenta profesional" Icon={CrossGlyph} disabled={loading} />
        </div>

        {formData.role === 'patient' && (
          <div style={s.nutriBox}>
            <p style={s.nutriHint}>Como paciente, te asignas al correo de tu nutriólogo.</p>
            <Field label="Correo de tu nutriólogo" name="nutritionist_email" type="email"
              value={formData.nutritionist_email} onChange={handleChange}
              error={errors.nutritionist_email || (nutri.valid === false && formData.nutritionist_email ? nutri.message : '')}
              disabled={loading} required />
            <div style={{ marginTop: -6, marginBottom: 4, minHeight: 18, display: 'flex', alignItems: 'center', gap: 7 }}>
              {nutriEnd}
              {nutri.valid && nutri.nutritionist && (
                <span style={s.nutriOk}>Nutriólogo encontrado: <strong>{nutri.nutritionist.name}</strong></span>
              )}
            </div>
          </div>
        )}

        <SectionTitle>Seguridad</SectionTitle>
        <PasswordField label="Contraseña" name="password" value={formData.password}
          onChange={handleChange} error={errors.password} disabled={loading}
          hint="Mínimo 8 caracteres, con mayúscula, minúscula y número." />
        {passwordStrength && formData.password && (
          <div style={{ margin: '-6px 0 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={s.pwLabel}>{passwordStrength.text}</span>
              <span style={s.pwLabel}>{passwordStrength.percent}%</span>
            </div>
            <div style={s.pwTrack}>
              <div style={{ height: '100%', width: `${passwordStrength.percent}%`, background: passwordStrength.color, borderRadius: 999, transition: 'width 0.25s' }} />
            </div>
          </div>
        )}
        <PasswordField label="Confirmar contraseña" name="confirm_password" value={formData.confirm_password}
          onChange={handleChange} error={errors.confirm_password} disabled={loading} hint="Repite tu contraseña." />

        <button type="submit" disabled={loading} className="cdx-btn cdx-btn--primary" style={s.submit}>
          {loading ? 'Creando tu cuenta…' : '✦ Crear mi cuenta gratis'}
        </button>

        <p style={s.footText}>
          ¿Ya tienes cuenta?{' '}
          <Link to="/auth/login" style={s.strongLink}>Iniciar sesión</Link>
        </p>
      </form>
    </AuthShell>
  );
};

const SectionTitle = ({ children }) => (
  <h3 style={{ fontFamily: 'var(--cdx-font-display)', fontSize: 16, fontWeight: 600, color: 'var(--cdx-jade-deep)', margin: '18px 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
    <span style={{ width: 18, height: 2, background: 'var(--cdx-cempasuchil)', borderRadius: 2 }} />
    {children}
  </h3>
);

const RoleCard = ({ active, onClick, title, sub, Icon, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} role="radio" aria-checked={active}
    style={{
      flex: 1, display: 'flex', alignItems: 'center', gap: 11, padding: '13px 14px', textAlign: 'left',
      borderRadius: 'var(--cdx-r-md)', cursor: 'pointer', transition: 'all 0.18s',
      border: active ? '1.5px solid var(--cdx-jade)' : '1px solid var(--cdx-paper-edge)',
      background: active ? 'var(--cdx-jade-wash)' : 'var(--cdx-paper-2)',
    }}>
    <span style={{ color: active ? 'var(--cdx-jade)' : 'var(--cdx-agave)', display: 'flex' }}><Icon /></span>
    <span>
      <span style={{ display: 'block', fontWeight: 700, fontSize: 14, color: 'var(--cdx-ink)' }}>{title}</span>
      <span style={{ display: 'block', fontSize: 12, color: 'var(--cdx-ink-faint)' }}>{sub}</span>
    </span>
  </button>
);

const PersonGlyph = () => (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-6 8-6s8 2 8 6" strokeLinecap="round" /></svg>);
const CrossGlyph = () => (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden><rect x="4" y="4" width="16" height="16" rx="4" /><path d="M12 8v8M8 12h8" strokeLinecap="round" /></svg>);
const OkGlyph = () => (<svg width="18" height="18" viewBox="0 0 20 20" fill="var(--cdx-jade-bright)" aria-hidden><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.7-9.3a1 1 0 00-1.4-1.4L9 10.6 7.7 9.3a1 1 0 10-1.4 1.4l2 2a1 1 0 001.4 0l4-4z" clipRule="evenodd" /></svg>);
const BadGlyph = () => (<svg width="18" height="18" viewBox="0 0 20 20" fill="var(--cdx-jamaica)" aria-hidden><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.7 7.3a1 1 0 00-1.4 1.4L8.6 10l-1.3 1.3a1 1 0 101.4 1.4L10 11.4l1.3 1.3a1 1 0 001.4-1.4L11.4 10l1.3-1.3a1 1 0 10-1.4-1.4L10 8.6 8.7 7.3z" clipRule="evenodd" /></svg>);
const Spinner = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden style={{ animation: 'spin 0.8s linear infinite' }}>
    <circle cx="12" cy="12" r="9" stroke="var(--cdx-paper-edge)" strokeWidth="3" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="var(--cdx-jade)" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const s = {
  error: { background: 'var(--cdx-jamaica-wash)', color: 'var(--cdx-jamaica)', border: '1px solid var(--cdx-jamaica-soft)', borderRadius: 'var(--cdx-r-md)', padding: '10px 14px', fontSize: 13.5, marginBottom: 14 },
  row: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  roleRow: { display: 'flex', gap: 10, marginBottom: 14 },
  nutriBox: { background: 'var(--cdx-turq-wash)', border: '1px solid color-mix(in srgb, var(--cdx-turquesa) 30%, transparent)', borderRadius: 'var(--cdx-r-md)', padding: '14px 14px 8px', marginBottom: 14 },
  nutriHint: { fontSize: 12.5, color: 'var(--cdx-turquesa)', fontWeight: 600, margin: '0 0 10px' },
  nutriOk: { fontSize: 12.5, color: 'var(--cdx-jade-deep)' },
  pwLabel: { fontSize: 12, fontWeight: 600, color: 'var(--cdx-ink-soft)' },
  pwTrack: { height: 7, borderRadius: 999, background: 'var(--cdx-paper-3)', overflow: 'hidden' },
  submit: { width: '100%', padding: '15px', fontSize: 16, marginTop: 18 },
  footText: { textAlign: 'center', fontSize: 14, color: 'var(--cdx-ink-soft)', marginTop: 18 },
  strongLink: { color: 'var(--cdx-copal)', fontWeight: 700, textDecoration: 'none' },
};

export default Register;
